import { Ambulance, Stethoscope, Home, HeartPulse, Sparkles, ArrowUpRight } from "lucide-react";
import { useReveal } from "../lib/useReveal";

const services = [
  {
    icon: Ambulance,
    title: "Animal Rescue",
    desc: "On-ground emergency rescue for injured or abandoned animals across the city. Our ambulance reaches within hours — no matter the weather, no matter the hour.",
    features: ["24/7 dispatch", "Trained handlers", "Free pick-up"],
    img: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/px9cdls2_image.png",
  },
  {
    icon: Stethoscope,
    title: "Veterinary Treatment",
    desc: "Full in-house hospital with surgery, diagnostics and long-term medication under qualified veterinarians.",
    features: ["Surgery & ICU", "Diagnostics", "Specialist vets"],
    img: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/rliybgkz_WhatsApp%20Image%202026-04-24%20at%204.15.49%20PM.jpeg",
  },
  {
    icon: Home,
    title: "Panjrapole Care",
    desc: "A lifelong home for every animal that walks through our gates — cows, bulls, calves, buffaloes, dogs, horses, camels, rabbits, goats and birds. Organic fodder, clean drinking water, shaded sheds and respectful ageing for all animals, without exception.",
    features: ["Organic fodder", "Daily grooming", "Spacious sheds", "Care for all animals"],
    img: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/rrx2tsxl_WhatsApp%20Image%202026-04-25%20at%2012.15.56%20AM.jpeg",
  },
  {
    icon: HeartPulse,
    title: "Rehabilitation",
    desc: "Post-surgery recovery and behavioural rehabilitation for dogs, deer and rabbits. Each animal gets a personalised care plan — nutrition, physiotherapy, quiet enclosures, and when possible, a safe release back into the wild or a loving home.",
    features: ["Physio care", "Diet programmes", "Safe release", "Adoption prep"],
    img: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/kykpvedl_WhatsApp%20Image%202026-04-24%20at%204.05.20%20PM%20%281%29.jpeg",
  },
  {
    icon: Sparkles,
    title: "Emergency Services",
    desc: "A dedicated helpline for road accidents, poisonings, orphaned calves, snake bites and extreme-weather distress. Call us anytime — trained volunteers, on-field first aid and rapid transport to our hospital are all standing by around the clock.",
    features: ["Hotline 24/7", "On-field first aid", "Rapid transport", "Free rescue"],
    img: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/xthqzukj_WhatsApp%20Image%202026-04-24%20at%204.05.20%20PM%20%287%29.jpeg",
  },
];

function Card({ s, i }) {
  const ref = useReveal();
  const Icon = s.icon;
  const spanClasses = s.featured
    ? "md:col-span-2 md:row-span-2"
    : "md:col-span-1";

  return (
    <article
      ref={ref}
      data-testid={`service-card-${i}`}
      className={`reveal group relative overflow-hidden rounded-2xl border border-[#E3DECE] bg-[#FDFBF7] hover:-translate-y-1 transition-all duration-500 hover:shadow-[0_20px_40px_-20px_rgba(27,77,62,0.25)] ${spanClasses}`}
      style={{ transitionDelay: `${i * 60}ms` }}
    >
      {s.img && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={s.img}
            alt={s.title}
            className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.06]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1B4D3E]/70 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-[#FDFBF7] flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#1B4D3E]" />
          </div>
        </div>
      )}
      <div className="p-8 md:p-10">
        {!s.img && (
          <div className="w-11 h-11 rounded-full bg-[#1B4D3E] flex items-center justify-center mb-6">
            <Icon className="w-5 h-5 text-[#D4AF37]" />
          </div>
        )}
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-2xl md:text-3xl text-[#1A2E24] leading-tight">
            {s.title}
          </h3>
          <ArrowUpRight className="w-5 h-5 text-[#7A8C81] group-hover:text-[#D4AF37] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
        </div>
        <p className="mt-4 text-[#4A5D53] leading-relaxed">{s.desc}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {s.features.map((f) => (
            <span
              key={f}
              className="text-[11px] tracking-[0.18em] uppercase text-[#1B4D3E] bg-[#F4F1EA] border border-[#E3DECE] px-3 py-1.5 rounded-full"
            >
              {f}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  const headRef = useReveal();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={headRef} className="reveal max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-[#D4AF37]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#4A5D53]">
              What we do
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A2E24] leading-[1.05]">
            A full spectrum of care — <span className="italic text-[#1B4D3E]">from rescue to release.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-fr">
          {services.map((s, i) => (
            <Card key={s.title} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
